import { selector } from 'lib/selectors';

import find from 'lodash/find';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import { documentsWithUniqLinked, sorterByFavoriteOrCreated } from './helpers';
import {
  isDokkaSupportSelector,
  userGUIDSelector,
  userIdSelector,
  payModuleCompaniesContextSelector,
} from 'domain/env/envSelector';
import {
  chatParticipantsSelector,
  chatParticipantsByIdSelector,
  chatUsersCompanyIdSelector,
} from 'domain/chat/chatSelector';
import CONST, { WS_GRID_DEFAULT_PRESET_ID, WS_GRID_PRESET_TYPES } from './constants';
import { wsGridPresetAdapter } from './adapters';
import { currentConnectedERPselector } from 'domain/settings';

import { categoriesListSelector } from 'domain/categories';

import {
  companiesByIdSelector,
  companyFeaturesCompanyIdSelector,
  companyGeneralFeatureFlagsSelector,
  companySigningsCompanyIdSelector,
  configurationCompanySelector,
} from '../companies';
import { erpsSelector } from '../settings';

import { CachedDocumentFactory } from 'domain/documents/factories';
import { textractEnabledSelector } from 'domain/textract';
import { rtlEnable } from 'domain/env';
import { textractForVendorEnabledSelector } from 'domain/journal/selectors';

const documents = (state) => state.documents;

export const tokenSelector = selector(documents, (d) => d.dokkaToken);
export const editableDocSelector = selector(documents, (d) => d.editableDoc);
export const recentTagsSelector = selector(documents, (d) => d.recentTags);
export const recentTagsCompanyIdSelector = selector(documents, (d) => d.recentTagsCompanyId);
export const pageTokenSelector = selector(documents, (d) => d.pageToken);
export const documentsByIdSelector = selector(documents, (d) => d.list);

export const aggregationsSelector = selector(documents, (d) => d.aggregations);
export const subtypeFilterSelector = selector(documents, (d) => d.subtypeFilter);
export const linkedSelector = selector(documents, (d) => d.linked);
export const linkingTagSelector = selector(documents, (d) => d.linkingTag);
export const documentLinkedSelector = selector(linkedSelector, (l) => l.list);
export const documentLinkedOpenStatusSelector = selector(linkedSelector, (l) => l.isOpen && !!l.list.size);
export const documentsCurrentCountSelector = selector(documents, (d) => d.company_total);
export const processingDocsCountSelector = selector(documents, (d) => d.processing);
export const getDocumentsForBulkApprove = (documentIds) =>
  selector(documentsByIdSelector, userGUIDSelector, (documentList, uID) =>
    documentList.filter((d) => {
      const next = d.getIn(['approvals', 'next']);
      const currentSigners = Array.isArray(next) ? next : [next];
      const approvalStatus = d.getIn(['approvals', 'status']);
      const isFlowActive = approvalStatus !== 'draft' && approvalStatus !== null;
      const isForbiddenTags = [CONST.tags.isScheduledApproval, CONST.tags.isScheduledAcceptance].some((tag) =>
        d.tags.has(tag),
      );

      return documentIds.includes(d.documentID) && !isForbiddenTags && currentSigners.includes(uID) && isFlowActive;
    }),
  );
export const getDocumentsForBulkPaid = (documentIds) =>
  selector(documentsByIdSelector, (documentList) =>
    documentList.filter((d) => {
      // if b-end dont detect paid status of document, by default we mark as UNPAID, and we can change to PAID
      const isUnknownType = ![CONST.tags.unpaid, CONST.tags.paid].some((t) => d.tags.has(t));

      return (
        documentIds.includes(d.documentID) &&
        d.tags.has(CONST.tags.financial) &&
        (d.tags.has(CONST.tags.unpaid) || isUnknownType)
      );
    }),
  );
export const getDocumentsForBulkUnpaid = (documentIds) =>
  selector(documentsByIdSelector, (documentList) =>
    documentList.filter(
      (d) => documentIds.includes(d.documentID) && d.tags.has(CONST.tags.financial) && d.tags.has(CONST.tags.paid),
    ),
  );
export const getDocumentsForBulkPermanentlyDeleteByCreator = (documentIds) =>
  selector(documentsByIdSelector, userIdSelector, (documentList, userId) =>
    documentList.filter((d) => {
      return documentIds.includes(d.documentID) && d.tags.has(CONST.tags.trash) && d.creator === userId;
    }),
  );
export const getDocumentsForBulkActivateApprovalFlow = (documentIds) =>
  selector(documentsByIdSelector, (documentList) =>
    documentList.filter((d) => documentIds.includes(d.documentID) && d.approvals?.status === 'draft'),
  );

export const getDocumentsForBulkDeactivateApprovalFlow = (documentIds) =>
  selector(documentsByIdSelector, (documentList) =>
    documentList.filter((d) => documentIds.includes(d.documentID) && d.approvals?.status === 'active'),
  );

export const getDocumentsForBulkSendToPay = (documentIds) =>
  selector(
    documentsByIdSelector,
    currentCompanyIdSelector,
    payModuleCompaniesContextSelector,
    companyGeneralFeatureFlagsSelector,
    (documentList, companyId, payModuleCompanies, companyFeatures) => {
      // Non-published (not yet accepted) invoices are eligible only when the company
      // enables `enable_prepare_payment_for_non_published_invoices`.
      const allowNonPublished = companyFeatures.get('enable_prepare_payment_for_non_published_invoices', false);

      return documentList.filter(
        (d) =>
          documentIds.includes(d.documentID) &&
          companyId &&
          payModuleCompanies.includes(companyId) &&
          d.doctype === 'financial' &&
          (allowNonPublished || d.tags.has(CONST.tags.isAccepted)) &&
          !d.tags.has(CONST.tags.isBeingProcessed) &&
          !d.tags.has(CONST.tags.paymentInProgress),
      );
    },
  );

export const getDocumentsForBulkApproverReplace = (documentIds, user) =>
  selector(documentsByIdSelector, (documentList) =>
    documentList
      .filter((d) => {
        if (documentIds.includes(d.documentID) && d.approvals?.nodes && !d.tags.has(CONST.tags.approvalComplete)) {
          const a = d.approvals.nodes.filter(
            ({ status, username }) => username === user && (status === 'pending' || status === 'draft'),
          );
          return !!a.length;
        }
        return false;
      })
      .keySeq()
      .toArray(),
  );
// check document ids if they are mixed (single document and linked are together)
export const isMixedDocumentsForBulkMoveToCompanySelector = (documentIds) =>
  selector(documentsByIdSelector, (documentList) =>
    Boolean(
      documentList
        .filter((d) => documentIds.includes(d.documentID) && d.linkid)
        .keySeq()
        .toArray().length,
    ),
  );

export const savedGridDefaultColumnsSelector = selector(documents, (d) => d.gridDefaultColumns);

export const savedGridFiltersSelector = selector(documents, (d) => d.gridFilters);
export const savedGridSortingSelector = selector(documents, (d) => d.gridSorting);
export const savedGridColumnsSelector = selector(documents, (d) => d.gridColumns);
export const getGridDocumentsByPageIndexSelector = selector(documents, (d) => d.gridDocumentsByPageIndex);
export const getGridSearchAfterByPageIndexSelector = selector(documents, (d) => d.gridSearchAfterByPageIndex);

export const currentCompanyIdSelector = selector(documents, (d) => d.companyId);
export const gridRawPresetsSelector = selector(documents, (d) => d.gridPresets);
export const gridRawPresetsLoadedSelector = selector(documents, (d) => d.gridPresetsLoaded);
export const gridSharePresetSelector = selector(documents, (d) => d.sharePreset);

export const gridDefaultPresetSelector = selector(
  savedGridDefaultColumnsSelector,
  userGUIDSelector,
  currentCompanyIdSelector,
  currentConnectedERPselector,
  (gridDefaultColumns, userId, companyId, erpType) =>
    wsGridPresetAdapter({
      id: WS_GRID_DEFAULT_PRESET_ID,
      name: 'Default',
      user_id: userId,
      company_id: companyId,
      erp_type: erpType,
      type: WS_GRID_PRESET_TYPES.default,
      config: {
        gridColumns: gridDefaultColumns || [],
        gridFilters: {},
        gridSorting: [],
      },
    }),
);

export const gridPresetsSelector = selector(
  gridRawPresetsSelector,
  gridSharePresetSelector,
  gridDefaultPresetSelector,
  (gridPresets, sharePreset, gridDefaultPreset) => {
    const shared = sharePreset ? [sharePreset] : [];
    return [gridDefaultPreset, ...shared, ...gridPresets];
  },
);

export const gridCurrentPresetIdSelector = selector(documents, (d) => d.currentPresetId);

export const gridCurrentPresetSelector = selector(
  gridCurrentPresetIdSelector,
  gridPresetsSelector,
  (id, presets) => find(presets, (p) => p.id === id) || presets[0],
);

export const gridCurrentFiltersSelector = selector(
  gridCurrentPresetSelector,
  savedGridFiltersSelector,
  (currentPreset, changedGridFilters) => {
    const { gridFilters = null } = get(currentPreset, 'config', {});

    return changedGridFilters || gridFilters || {};
  },
);

export const gridCurrentSortSelector = selector(
  gridCurrentPresetSelector,
  savedGridSortingSelector,
  (currentPreset, changedGridSorting) => {
    const { gridSorting = null } = get(currentPreset, 'config', {});
    return changedGridSorting || gridSorting || [];
  },
);

export const gridCurrentColumnsStateSelector = selector(
  gridCurrentPresetSelector,
  savedGridDefaultColumnsSelector,
  savedGridColumnsSelector,
  (currentPreset, defaultGridColumnsState, changedGridColumnsState) => {
    const { gridColumns = null } = get(currentPreset, 'config', {});
    return changedGridColumnsState || gridColumns || defaultGridColumnsState || null;
  },
);

export const gridCurrentSettingsStateSelector = selector(
  gridCurrentFiltersSelector,
  gridCurrentSortSelector,
  gridCurrentColumnsStateSelector,
  (gridFilters, gridSorting, gridColumnsState) => ({ gridFilters, gridSorting, gridColumnsState }),
);

export const isFilterChangeSelector = selector(
  gridCurrentPresetSelector,
  gridCurrentFiltersSelector,
  (currentPreset, currentFilters) => {
    const { gridFilters } = get(currentPreset, 'config', {});
    return !isEqual(gridFilters, currentFilters);
  },
);

export const isSortChangeSelector = selector(
  gridCurrentPresetSelector,
  gridCurrentSortSelector,
  (currentPreset, currentSorting) => {
    const { gridSorting } = get(currentPreset, 'config', {});

    return !isEqual(gridSorting, currentSorting);
  },
);

export const isColumnsChangeSelector = selector(
  gridCurrentPresetSelector,
  gridCurrentColumnsStateSelector,
  (currentPreset, currentColumns) => {
    const { gridColumns } = get(currentPreset, 'config', {});

    return !isEqual(gridColumns || [], currentColumns || []);
  },
);
// documents List that need to be updated - after bulk actions
export const gridApplyTransactionSelector = selector(documents, (d) => d.gridApplyTransaction);

export const justPublishedDocumentSelector = selector(documents, (d) => d.justPublished);
export const loadedSelector = selector(documents, (d) => d.loaded);
export const documentSortedLinkedSelector = selector(documentLinkedSelector, (d) =>
  d.toList().sort(sorterByFavoriteOrCreated),
);
export const unreadRequestsLoadedSelector = selector(documents, (d) => d.unreadRequestsLoaded);
export const isLinkedDocsLockedSelector = selector(documentLinkedSelector, (docs) =>
  docs.some((doc) => doc.linkid.endsWith('locked')),
);
export const documentLinkedTagSelector = selector(linkedSelector, (d) => d.tag);
export const documentSelector = selector(documents, (d) => d.document);
export const documentStatusesSelector = selector(documents, (d) => d.document.status);
export const documentLastModifiedDateSelector = selector(documents, (d) => d.document.lastModifiedDate);
export const documentDocumentIdSelector = selector(documents, (d) => d.document.documentID);
export const documentDoctypeSelector = selector(documents, (d) => d.document.doctype);
export const documentTagsSelector = selector(documents, (d) => d.document.tags);
export const documentCountSelector = selector(documents, (d) => d.count);
export const documentsListListSelector = selector(documentsByIdSelector, (d) => d.toList());
export const documentsSelector = selector(documentsListListSelector, (d) => documentsWithUniqLinked(d));
export const uploadQueueSelector = selector(documents, (d) => d.uploadQueue);

export const erpDuplicatedSelector = selector(documents, (d) => d.erpDuplicated);
export const unreadRequestsSelector = selector(documents, (d) => d.unreadRequests);
export const isDocumentConfidentialSelector = selector(documentSelector, (d) => d.tags.has('_S_CONFIDENTIAL'));
export const isDocumentPaidSelector = selector(documentSelector, (d) => d.tags.has(CONST.tags.paid));
export const isDocumentScheduledApprovalSelector = selector(documentSelector, (d) =>
  d.tags.has(CONST.tags.isScheduledApproval),
);

export const textractEnabledForDocumentSelector = selector(
  textractEnabledSelector,
  documentSelector,
  textractForVendorEnabledSelector,
  (textractEnabled, d, textractForVendorEnabled) =>
    textractEnabled &&
    textractForVendorEnabled &&
    !d.tags.has(CONST.tags.isAccepted) &&
    !d.tags.has(CONST.tags.isBeingProcessed),
);

// if textract toggler enabled we force default view only
export const viewArrangementSelector = selector(
  documents,
  textractEnabledForDocumentSelector,
  rtlEnable,
  (d, twoWayMatchingTogglerEnabled, rtl) => (twoWayMatchingTogglerEnabled ? (rtl ? 'rtl' : 'ltr') : d.viewArrangement),
);

export const totalNumberOfDocumentsSelector = selector(documents, (d) => d.count_without_link_panels);

export const isPendingUploadSelector = selector(uploadQueueSelector, (d) => d.some((it) => it.status === 'pending'));

export const documentsGridHeadersListSelector = selector(documents, (d) => d.gridHeadersList);

export const gridHasNewDocumentsSelector = selector(
  documents,
  (d) =>
    Boolean(d.fetchedDocumentsTimeStamp) &&
    Boolean(d.latestWorkerTimeStamp) &&
    d.fetchedDocumentsTimeStamp !== d.latestWorkerTimeStamp,
);

export const documentsGridLoaderSelector = selector(documents, (d) => d.gridLoader);
export const documentHotkeysSelector = selector(documents, (d) => d.hotkeys.data);
export const documentHotkeysCompanyIdSelector = selector(documents, (d) => d.hotkeys.companyId);
export const documentHotkeysIsLoadingSelector = selector(documents, (d) => d.hotkeys.isLoading);

export const cachedDocumentSelector = (docHash) =>
  selector(documents, (d) => d.cachedDocuments.find(({ hash }) => hash === docHash) || CachedDocumentFactory());

export const shouldRenewCompanyToken = (companyId) =>
  selector(tokenSelector, currentCompanyIdSelector, (token, currentCompany) => companyId !== currentCompany);

export const shouldProcessingDocBeBlockedSelector = selector(
  documentSelector,
  isDokkaSupportSelector,
  (d, isSupport) => d.tags.has(CONST.tags.isBeingProcessed) && !isSupport,
);

export const shouldRenewUsersList = (companyId) =>
  selector(
    (x) => x.organizationUser,
    currentCompanyIdSelector,
    (users, currentCompanyId) => !users.size || currentCompanyId !== companyId,
  );

export const shouldRenewCategories = (companyId) =>
  selector(
    categoriesListSelector,
    currentCompanyIdSelector,
    (list, currentCompanyId) => !list.size || currentCompanyId !== companyId,
  );

export const shouldRenewCompanyFeatures = (companyId) =>
  selector(companyFeaturesCompanyIdSelector, (featureSetCompanyId) => featureSetCompanyId !== companyId);
export const shouldRenewConfigurationCompany = (companyId) =>
  selector(
    configurationCompanySelector,
    (configurationCompany) => !configurationCompany.get('id') || configurationCompany.get('id') !== companyId,
  );

export const shouldRenewCompanySignings = (companyId) =>
  selector(companySigningsCompanyIdSelector, (signingsCompanyId) => signingsCompanyId !== companyId);

export const shouldRenewCompanyHotkeys = (companyId) =>
  selector(documentHotkeysCompanyIdSelector, (hotkeysCompanyId) => hotkeysCompanyId !== companyId);

// work only if the user went to the document page via a direct link
export const shouldRenewCompanyPreferences = (companyId) =>
  selector(
    companiesByIdSelector,
    documentsByIdSelector,
    (companies, docs) => !companies.getIn([companyId, 'signParams', 'guid']) && !docs.size,
  );

export const shouldRenewCompanyPreferencesOnWorkspace = (companyId) =>
  selector(companiesByIdSelector, (companies) => !companies.getIn([companyId, 'signParams', 'guid']));

export const shouldInvalidateDocumentCacheSelector = selector(documents, (d) => d.shouldInvalidateDocumentCache);

export const shouldRenewCompanyChatPears = (companyId) =>
  selector(
    chatParticipantsSelector,
    chatUsersCompanyIdSelector,
    (users, usersCompanyId) => !users.size || usersCompanyId !== companyId,
  );

export const shouldRenewCompanyConnections = () => selector(erpsSelector, (erps) => !erps.size);
export const shouldRenewExportFormats = () => selector(documents, (d) => !d.exportFormatsLoaded);
export const shouldRenewGridPresets = () => selector(gridRawPresetsLoadedSelector, (isLoaded) => !isLoaded);
export const shouldRenewUnreadRequests = () => selector(unreadRequestsLoadedSelector, (isLoaded) => !isLoaded);

export const shouldRenewMRUTags = (companyId) =>
  selector(recentTagsCompanyIdSelector, (recentTagsCompanyId) => recentTagsCompanyId !== companyId);

export const currentViewersSelector = selector(
  documents,
  chatParticipantsByIdSelector,
  userIdSelector,
  // exclude current user and ghost, as ghost is not presented in chat peers
  // no other way to tell if another user is ghost except for its absence in chat peers
  (d, users, currentUserId) => d.currentViewers.filter((userId) => userId !== currentUserId && users.has(userId)),
);
